<template>
  <div class="doorphone-activation">
    <div class="doorphone-activation__intro">
      <h1 class="doorphone-activation__title">Активация домофона</h1>
      <p class="doorphone-activation__subtitle">
        Сначала выберите домофон справа. Тестовая активация — без лицевого счёта и с автоудалением.
        Боевая — с привязкой в приложении и лицевым счётом.
      </p>
    </div>

    <v-row>
      <v-col cols="12" md="5" lg="4" class="pb-0">
        <v-alert
          v-if="selectedDoorphone"
          dense
          text
          color="primary"
          class="mb-4"
        >
          <div class="doorphone-activation__selected">
            <v-icon small color="primary" class="mr-1">mdi-doorbell-video</v-icon>
            <div>
              <div class="doorphone-activation__selected-id">Домофон №{{ selectedDoorphone.id }}</div>
              <div>{{ selectedDoorphone.address }}</div>
              <div v-if="selectedDoorphone.diapason" class="doorphone-activation__hint">
                Квартиры: {{ selectedDoorphone.diapason }}
              </div>
            </div>
          </div>
        </v-alert>
        <v-alert v-else dense text type="info" class="mb-4">
          Выберите домофон в таблице справа
        </v-alert>

        <v-card outlined class="doorphone-activation__card">
          <v-tabs
            v-model="activationTab"
            background-color="transparent"
            class="doorphone-activation__tabs"
          >
            <v-tab v-if="$checkPermission('test_activate_doorphone')" href="#test">
              <v-icon left small>mdi-account-clock-outline</v-icon>
              Тестовая
            </v-tab>
            <v-tab href="#lk">
              <v-icon left small>mdi-home-account</v-icon>
              Боевая
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activationTab">
            <v-tab-item value="test">
              <v-card-text>
                <p class="doorphone-activation__card-text">
                  Лицевой счёт не создаётся. Ключ создаётся автоматически.
                  По истечении срока активация удаляется.
                </p>
                <v-text-field
                  v-model="apartment"
                  label="Номер квартиры"
                  outlined
                  dense
                  type="tel"
                  prepend-inner-icon="mdi-home-account"
                  hide-details="auto"
                  class="mb-3 no-spin"
                />
                <v-text-field
                  v-model="telephone"
                  label="Телефон клиента"
                  outlined
                  dense
                  type="tel"
                  maxlength="11"
                  prepend-inner-icon="mdi-phone"
                  :rules="rules.requiredTelephone"
                  hint="11 цифр"
                  persistent-hint
                  class="mb-3 no-spin"
                >
                  <template v-if="myActivatePhone" v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="fillMyPhone('test')"
                        >
                          <v-icon small>mdi-cellphone</v-icon>
                        </v-btn>
                      </template>
                      <span>Мой телефон</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-select
                  :items="activationDaysItems"
                  item-text="text"
                  item-value="value"
                  v-model="activationDurationDays"
                  label="Срок активации"
                  outlined
                  dense
                  prepend-inner-icon="mdi-timer-outline"
                  hint="После срока тестовая активация удалится автоматически"
                  persistent-hint
                />
              </v-card-text>
              <v-card-actions class="px-4 pb-4">
                <v-btn
                  color="#4C6C93"
                  dark
                  class="text-none"
                  :loading="testLoading"
                  :disabled="testLoading || lkLoading"
                  @click="runTestActivate"
                >
                  <v-icon left small>mdi-account-clock-outline</v-icon>
                  Тестовая активация
                </v-btn>
                <v-spacer />
                <v-btn text color="grey darken-1" class="text-none" @click="resetTestForm">
                  Сбросить
                </v-btn>
              </v-card-actions>
            </v-tab-item>
            <v-tab-item value="lk">
              <v-card-text>
                <v-alert dense text type="warning" class="mb-4">
                  Клиент должен быть зарегистрирован в мобильном приложении по этому телефону.
                  Если он ещё не заходил — сначала пусть зарегистрируется, затем повторите активацию.
                </v-alert>
                <p class="doorphone-activation__card-text">
                  Создаётся лицевой счёт. Желательно, чтобы у клиента был физический ключ — тогда введите его код.
                  Если ключ не нужен, поле можно оставить пустым: система создаст виртуальный ключ и добавит его на домофон.
                </p>
                <v-text-field
                  v-model="lkApartment"
                  label="Номер квартиры"
                  outlined
                  dense
                  type="tel"
                  prepend-inner-icon="mdi-home-account"
                  hide-details="auto"
                  class="mb-3 no-spin"
                />
                <v-text-field
                  v-model="lkTelephone"
                  label="Телефон клиента"
                  outlined
                  dense
                  type="tel"
                  maxlength="11"
                  prepend-inner-icon="mdi-phone"
                  :rules="rules.requiredTelephone"
                  hint="11 цифр, тот же номер, что в приложении"
                  persistent-hint
                  class="mb-3 no-spin"
                >
                  <template v-if="myActivatePhone" v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="fillMyPhone('lk')"
                        >
                          <v-icon small>mdi-cellphone</v-icon>
                        </v-btn>
                      </template>
                      <span>Мой телефон</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-text-field
                  v-model="lkKeyCode"
                  label="Код ключа"
                  outlined
                  dense
                  prepend-inner-icon="mdi-key-variant"
                  hint="Желательно, чтобы у клиента был физический ключ — введите его код. Если ключ не нужен, оставьте поле пустым: создастся виртуальный."
                  persistent-hint
                />
              </v-card-text>
              <v-card-actions class="px-4 pb-4">
                <v-btn
                  color="#2E7D32"
                  dark
                  class="text-none"
                  :loading="lkLoading"
                  :disabled="testLoading || lkLoading"
                  @click="runLkActivate"
                >
                  <v-icon left small>mdi-home-account</v-icon>
                  Боевая активация
                </v-btn>
                <v-spacer />
                <v-btn text color="grey darken-1" class="text-none" @click="resetLkForm">
                  Сбросить
                </v-btn>
              </v-card-actions>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>

      <v-col cols="12" md="7" lg="8" class="pb-0">
        <v-card outlined class="doorphone-activation__table-card">
          <v-card-title class="doorphone-activation__table-title">
            <span>Домофоны</span>
            <v-text-field
              v-model="filterAddress"
              @keyup="validateKeyUp"
              label="Поиск по адресу или id"
              dense
              outlined
              clearable
              hide-details
              autocomplete="chrome-off"
              prepend-inner-icon="mdi-magnify"
              class="doorphone-activation__search"
            />
            <v-btn
              color="#4C6C93"
              dark
              class="text-none ml-2"
              @click.prevent="filterDoorphones"
            >
              Найти
            </v-btn>
          </v-card-title>
          <v-data-table
            class="table"
            v-model="selectedDoorphones"
            :options.sync="options"
            :server-items-length="totalDoorphones"
            :headers="headersDoorphones"
            :items="doorphones"
            item-value="id"
            return-object
            show-select
            :footer-props="{
              'items-per-page-options': [10, 20, 50, 100],
              showFirstLastPage: true,
            }"
            :single-select="true"
            :disable-sort="true"
          >
            <template v-slot:item.address="{ item }">
              {{ item.address }}
            </template>
            <template v-slot:item.sip_status="{ item }">
              <div>
                <v-icon x-small :color="item.sip_status_color">
                  mdi-checkbox-blank-circle
                </v-icon>
                {{ item.sip_status_text }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'DoorphoneTestActivation',
  data: () => ({
    isCreated: false,
    activationTab: 'test',
    apartment: null,
    telephone: null,
    activationDurationDays: 1,
    activationDaysItems: [
      { text: '1 день', value: 1 },
      { text: '3 дня', value: 3 },
      { text: '7 дней', value: 7 },
    ],
    lkApartment: null,
    lkTelephone: null,
    lkKeyCode: '',
    testLoading: false,
    lkLoading: false,
    selectedDoorphones: [],
    totalDoorphones: 0,
    filterAddress: '',
    options: {
      itemsPerPage: 10,
    },
    filters: { status: 'null,0,1,2,3,4,5,6,7,-1', type: 'A,M,G' },
    sort: '',
    rules: {
      requiredTelephone: [
        v => !v || /^\d{11}$/.test(v) || 'Введите 11 цифр',
      ],
    },
    headersDoorphones: [
      { text: 'id', value: 'id' },
      { text: 'Адрес', value: 'address' },
      { text: 'Агент', value: 'agent' },
      { text: 'Управляющая компания', value: 'management_company' },
      { text: 'Статус', value: 'sip_status' },
    ],
  }),
  watch: {
    options: {
      handler () {
        if (!this.isCreated) {
          this.updateSort();
          this.loadDoorphones();
        } else {
          this.isCreated = false;
        }
      },
      deep: true,
    },
    apartment (value) {
      if (this.lkApartment == null || this.lkApartment === '') {
        this.lkApartment = value;
      }
    },
    telephone (value) {
      if (this.lkTelephone == null || this.lkTelephone === '') {
        this.lkTelephone = value;
      }
    },
  },
  created () {
    this.isCreated = true;
    this.sort = ['-id'];
    if (Object.keys(this.$route.query).length) {
      const routeQuery = this.$route.query;
      if (routeQuery.address) {
        this.filterAddress = routeQuery.address;
        this.filters.address = routeQuery.address;
      }
    }
    this.loadDoorphones();
    if (!this.$checkPermission('test_activate_doorphone') && this.$checkPermission('lk_activate_doorphone')) {
      this.activationTab = 'lk';
    }
    if (this.userId) {
      this.loadUser(this.userId);
    }
  },
  computed: {
    ...mapGetters({
      doorphones: 'Doorphone/doorphones',
      userId: 'userId',
      profile: 'User/user',
    }),
    myActivatePhone () {
      return String(this.profile.test_phone_activate || '').replace(/\D/g, '').slice(0, 11);
    },
    selectedDoorphone () {
      return this.selectedDoorphones[0] || null;
    },
  },
  methods: {
    ...mapActions({
      getDoorphones: 'Doorphone/getDoorphones',
      testActivateDoorphone: 'Doorphone/testActivateDoorphone',
      lkActivateDoorphone: 'Doorphone/lkActivateDoorphone',
      loadUser: 'User/loadUser',
    }),
    fillMyPhone (form) {
      if (!this.myActivatePhone) {
        return;
      }
      if (form === 'lk') {
        this.lkTelephone = this.myActivatePhone;
        return;
      }
      this.telephone = this.myActivatePhone;
    },
    validateKeyUp (e) {
      if (e.keyCode === 13) {
        this.filterDoorphones();
      }
    },
    requireDoorphone () {
      if (!this.selectedDoorphone) {
        this.$flash_danger('Выберите домофон');
        return null;
      }
      return this.selectedDoorphone.id;
    },
    activationError (err, fallback) {
      if (err.response && err.response.data && err.response.data.error) {
        this.$flash_warning(err.response.data.error);
        return;
      }
      this.$flash_warning(fallback);
    },
    runTestActivate () {
      const doorphoneId = this.requireDoorphone();
      if (!doorphoneId) {
        return;
      }
      const apt = Number(this.apartment);
      const phone = String(this.telephone || '').replace(/\D/g, '');
      if (!apt || apt <= 0) {
        this.$flash_danger('Введите номер квартиры');
        return;
      }
      if (!/^\d{11}$/.test(phone)) {
        this.$flash_danger('Введите телефон клиента, 11 цифр');
        return;
      }
      if (![1, 3, 7].includes(Number(this.activationDurationDays))) {
        this.$flash_danger('Выберите срок 1, 3 или 7 дней');
        return;
      }

      this.testLoading = true;
      this.testActivateDoorphone({
        doorphoneId,
        form: {
          apartment: apt,
          phone,
          activation_duration_days: Number(this.activationDurationDays),
        },
      })
        .then(() => {
          this.$flash_success('Тестовая активация создана. Лицевой счёт не создавался.');
        })
        .catch((err) => {
          this.activationError(err, 'Не удалось выполнить тестовую активацию');
        })
        .finally(() => {
          this.testLoading = false;
        });
    },
    runLkActivate () {
      const doorphoneId = this.requireDoorphone();
      if (!doorphoneId) {
        return;
      }
      const apt = Number(this.lkApartment);
      const phone = String(this.lkTelephone || '').replace(/\D/g, '');
      const keyCode = String(this.lkKeyCode || '').trim();
      if (!apt || apt <= 0) {
        this.$flash_danger('Введите номер квартиры');
        return;
      }
      if (!/^\d{11}$/.test(phone)) {
        this.$flash_danger('Введите телефон клиента, 11 цифр');
        return;
      }

      this.lkLoading = true;
      this.lkActivateDoorphone({
        doorphoneId,
        form: {
          apartment: apt,
          phone,
          key_code: keyCode || undefined,
        },
      })
        .then((data) => {
          const parts = [data.message || 'Домофон привязан'];
          if (data.key_created && data.key_code) {
            parts.push(`Виртуальный ключ: ${data.key_code}`);
          }
          if (data.client_created) {
            parts.push('Создан клиент в домофонии');
          }
          this.$flash_success(parts.join('. '));
        })
        .catch((err) => {
          this.activationError(err, 'Не удалось выполнить боевую активацию');
        })
        .finally(() => {
          this.lkLoading = false;
        });
    },
    resetTestForm () {
      this.apartment = null;
      this.telephone = null;
      this.activationDurationDays = 1;
    },
    resetLkForm () {
      this.lkApartment = null;
      this.lkTelephone = null;
      this.lkKeyCode = '';
    },
    loadDoorphones () {
      this.getDoorphones({
        pagination: 1,
        page: this.options.page,
        limit: this.options.itemsPerPage,
        sort: this.sort,
        filters: this.filters,
      })
        .then((response) => {
          this.totalDoorphones = response.meta.total;
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке домофонов');
        });
    },
    filterDoorphones () {
      this.options.page = 1;
      this.filters = { status: 'null,0,1,2,3,4,5,6,7,-1', type: 'A,M,G' };

      if (this.filterAddress) {
        if (!isNaN(this.filterAddress)) {
          this.filters.id = this.filterAddress;
          delete this.filters.address;
        } else {
          this.filters.address = this.filterAddress;
          delete this.filters.id;
        }
      }
      this.$router.push({
        query: {
          address: this.filters.address,
        },
      }).catch(() => {});
      this.loadDoorphones();
    },
    updateSort () {
      if (this.options.sortBy.length > 0) {
        if ((this.options.sortDesc.length > 0) && (this.options.sortDesc[0])) {
          this.sort = '-' + this.options.sortBy[0];
        } else {
          this.sort = this.options.sortBy[0];
        }
      } else {
        this.sort = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.doorphone-activation__title {
  font-size: 1.35rem;
  font-weight: 500;
  color: #37474f;
  margin: 0 0 6px;
}

.doorphone-activation__subtitle {
  color: #607d8b;
  margin: 0 0 20px;
  max-width: 720px;
  line-height: 1.45;
}

.doorphone-activation__card {
  border-radius: 12px;
  overflow: hidden;
}

.doorphone-activation__tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.doorphone-activation ::v-deep .no-spin input {
  -moz-appearance: textfield;
}

.doorphone-activation ::v-deep .no-spin input::-webkit-outer-spin-button,
.doorphone-activation ::v-deep .no-spin input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.doorphone-activation__card-text {
  color: #546e7a;
  margin-bottom: 16px;
  line-height: 1.45;
}

.doorphone-activation__selected {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.doorphone-activation__selected-id {
  font-weight: 600;
}

.doorphone-activation__hint {
  font-size: 12px;
  opacity: 0.8;
}

.doorphone-activation__table-card {
  border-radius: 12px;
}

.doorphone-activation__table-title {
  flex-wrap: wrap;
  gap: 8px;
}

.doorphone-activation__search {
  max-width: 420px;
  flex: 1;
  margin: 0 0 0 12px;
}
</style>
